/**
 * Lane W shared types.
 *
 * The data types come from the shared episode-script types lane P landed
 * (`dorian-shared/types/episodeScript`, spec section 8.1) and are ALIASED here, not restated, so
 * that lane U can hand this lane the very objects the core produced (amendment A5: one
 * implementation, one set of types). Only the types this lane itself owns — the http seam, the
 * apply state and the refusal vocabulary of section 8.4 — are declared here.
 */

import type {
  Answer,
  Branch,
  Digest,
  EditContextAnswerPatch as SharedEditContextAnswerPatch,
  EditContextNodePatch as SharedEditContextNodePatch,
  EditContextPatch,
  EditContextStepPatch as SharedEditContextStepPatch,
  EngineId as SharedEngineId,
  Fixture,
  Plan,
  RowCounts as SharedRowCounts,
  Step,
  StructuralDelta as SharedStructuralDelta,
  Verdict as SharedVerdict,
  VerifiablePlan as SharedVerifiablePlan,
} from '../../../../dorian-shared/types/episodeScript';
import type { VerifyClient } from '../../../../services/EpisodeScriptVerifyService/verifyClient';
import type {
  VerifierArtifactIdentity as SharedVerifierArtifactIdentity,
  VerifierCanonSet,
} from '../../../../services/EpisodeScriptVerifyService/workerProtocol';

/** The engine's id union: the planner mints strings, the compiler mints negatives (types :23). */
export type EngineId = SharedEngineId;

/** A projection digest pair `[branchCount, hash]`. The audit record keeps it width-free. */
export type BasilDigest = readonly number[];

export type BasilAnswer = Answer;
export type BasilStep = Step;
export type BasilBranch = Branch;
export type BasilFixture = Fixture;

/**
 * The raw full read of record, `GET /v1/stories/:id/branches`. The shared `Fixture` already carries
 * `success`, `branches` and `serverTime` (types :732-736), so the read of record IS that shape.
 */
export type BranchesRead = Fixture;

export type RowCounts = SharedRowCounts;
export type StructuralDelta = SharedStructuralDelta;

/** `edit_context.js:250-273`. */
export interface EditContextWarning {
  nodeId?: EngineId;
  stepId?: EngineId;
  answerId?: EngineId;
  field: string;
  message: string;
}

/**
 * `edit_context.js:560-564`. `warnings` is added ONLY when non-empty, so it is OPTIONAL here.
 *
 * MESSAGE TO LANE P: the shared `EditContextReply` (types :556-559) declares `warnings` required
 * and typed `unknown[]`; spec section 5.6 says the port makes it optional. Until it does, the write
 * lane reads the reply through this local shape, which is the wire truth.
 */
export interface EditContextReply {
  success?: boolean;
  updatedNodes?: number;
  updatedSteps?: number;
  updatedAnswers?: number;
  warnings?: EditContextWarning[];
  error?: string;
  details?: Array<{ message: string }>;
}

export type EditContextAnswerPatch = SharedEditContextAnswerPatch;
export type EditContextStepPatch = SharedEditContextStepPatch;
export type EditContextNodePatch = SharedEditContextNodePatch;
export type EditContextBody = EditContextPatch;

/** The fields `verify` reads of a plan (types :1601-1607). */
export type VerifiablePlan = SharedVerifiablePlan;

/** `createPlan`'s return (plan.ts:433-524). */
export type BasilPlan = Plan;

export interface DigestSet {
  v1: Digest;
  v2: Digest;
  layout: Digest;
}

/** `verify`'s return (types :1666-1679). */
export type Verdict = SharedVerdict;

/** Recorded in every audit entry (BAR-SFR-01 point 1). */
export type VerifierArtifactIdentity = SharedVerifierArtifactIdentity;

/**
 * The verifier's own per-node canon lines and digests for one read, in the three canons the verdict
 * compares. It crosses the worker boundary; nothing in the main bundle computes it (ruling D2).
 */
export type BasilCanonSet = VerifierCanonSet;

/** The lane V seam (section 8.3) — the only verifier surface lane W touches. */
export type BasilVerifyClient = VerifyClient;

/** The response fields lane W reads; an AxiosResponse is assignable to it. */
export interface BasilHttpResponse<T> {
  data: T;
  status: number;
}

/**
 * The http seam. The page's axios instance (`components/api.js`) is assignable to it, and every
 * test supplies a mock: no lane W test ever issues a real request.
 */
export interface BasilHttp {
  get<T>(url: string): Promise<BasilHttpResponse<T>>;
  patch<T>(url: string, body?: unknown): Promise<BasilHttpResponse<T>>;
  post<T>(url: string, body?: unknown): Promise<BasilHttpResponse<T>>;
  put<T>(url: string, body?: unknown): Promise<BasilHttpResponse<T>>;
}

/** Section 8.4 — consumed by lane U. */
export type ApplyRefusal =
  | 'not-owner' | 'story-not-allowlisted' | 'story-not-draft' | 'live-denylist'
  | 'verifier-unavailable' | 'audit-unavailable' | 'plan-refused' | 'needs-canary'
  | 'needs-owner' | 'stale-baseline' | 'read-only' | 'nothing-to-apply'
  /**
   * the page's own subject moved under the apply: a different episode, group, namespace — or a
   * different authenticated account from the one the run is bound to.
   */
  | 'subject-changed'
  /**
   * the page cannot be re-read, so the subject this run is bound to cannot be re-proved before the
   * next body. The per-chunk re-read is the only thing that stops a story that went live, or an
   * account that changed, from receiving the rest of the queue; a run that cannot make it sends
   * nothing at all.
   */
  | 'subject-unavailable'
  /**
   * the verifier was alive at the handshake and is not alive now: it stopped answering, or the
   * artifact that answers is no longer the one this run identified. Distinct from
   * `verifier-unavailable`, which is a verifier that never answered at all.
   */
  | 'verifier-lost'
  /**
   * the verifier is answering but could not supply a complete canon, so the state of the episode
   * on one side of a write cannot be proven. Nothing further is sent.
   */
  | 'canon-unavailable';

/**
 * Section 8.4 — consumed by lane U.
 *
 * T17-08 adds `stopped`, and the distinction it draws is the writer's: `refused` is a LIMITATION —
 * this surface cannot make this kind of change, and no amount of trying again will help — while
 * `stopped` is a run that halted before this request's body was built, for a reason that will
 * usually not be true next time. Both sent nothing; only one of them is permanent, and telling a
 * writer "Can't do this here" about a verifier that stopped answering for a second is a lie about
 * their episode.
 */
export type RequestStatus =
  | 'planned' | 'refused' | 'needs-canary' | 'needs-owner' | 'stale' | 'stopped'
  | 'queued' | 'sending' | 'reconciled' | 'mismatched' | 'verified' | 'rejected';

/**
 * Whether this request's body actually went out, as the pipeline observed it (Basil v0.2, P4).
 *
 * It is an ANNOTATION on what already happened: nothing reads it to decide anything, no gate, no
 * order, no body and no audit record depends on it, and removing it would change no behaviour. It
 * exists because "could not be confirmed" is two different facts for a writer — a change that was
 * sent and one that was not — and the surface cannot tell them apart from a status word alone.
 *
 *  * `not-sent`   nothing left the browser for this request. Every pre-send refusal is this.
 *  * `sent`       the engine received it and answered; what it answered is the status's business.
 *  * `unknown`    the request was dispatched and the reply was lost, or is still in flight. It may
 *                 or may not have been applied, and the surface must claim neither.
 */
export type RequestDelivery = 'not-sent' | 'sent' | 'unknown';

export interface ApplyStatusEntry {
  status: RequestStatus;
  message?: string;
  chunkIndex: number;
  /** P4: what is known about the body itself. Absent means `not-sent`. */
  delivery?: RequestDelivery;
}

/** Section 8.4 — consumed by lane U. */
export interface ApplyState {
  phase: 'idle' | 'preflight' | 'sending' | 'rereading' | 'verifying' | 'done' | 'aborted';
  /** keyed by planId */
  statuses: Record<string, ApplyStatusEntry>;
  /** keyed by chunkIndex */
  verdicts: Record<number, Verdict>;
  refusal: ApplyRefusal | null;
  warnings: EditContextWarning[];
  details: Array<{ message: string }>;
}

export const EMPTY_APPLY_STATE: ApplyState = {
  phase: 'idle',
  statuses: {},
  verdicts: {},
  refusal: null,
  warnings: [],
  details: [],
};
